export const WEFAT_SYSTEM_INFO_API_NAMES = Object.freeze([
    'getSystemInfo',
    'getSystemInfoSync',
    'getWindowInfo',
    'getMenuButtonBoundingClientRect',
    'getDeviceInfo',
    'getAppBaseInfo',
    'getSystemSetting',
    'getAppAuthorizeSetting',
]);
const CALLBACK_KEYS = new Set(['success', 'fail', 'complete']);
const WEB_PLATFORM = 'web';
const NOT_DETERMINED = 'not determined';
export class WxSystemInfoRuntimeError extends Error {
    code;
    errCode;
    errMsg;
    api;
    remediation;
    constructor(code, api, reason, remediation, cause) {
        const errMsg = `${api}:fail ${reason}`;
        super(`${code} ${errMsg}`);
        this.name = 'WxSystemInfoRuntimeError';
        this.code = code;
        this.errCode = code;
        this.errMsg = errMsg;
        this.api = api;
        this.remediation = remediation;
        if (cause !== undefined) {
            Object.defineProperty(this, 'cause', {
                configurable: false,
                enumerable: false,
                value: cause,
                writable: false,
            });
        }
    }
}
function environmentError(api, reason, cause) {
    return new WxSystemInfoRuntimeError('WF6120', api, reason, 'Run this adapter in a browser environment or inject a complete BrowserSystemInfoEnvironment for the selected runtime.', cause);
}
function optionError(api, reason) {
    return new WxSystemInfoRuntimeError('WF6121', api, reason, 'Pass an optional plain object containing only success, fail, and complete callback functions.');
}
function isObjectLike(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}
function isPlainRecord(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
function finiteNumber(value, fallback) {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : fallback;
}
function readTheme(environment) {
    if (typeof document !== 'undefined' && document.documentElement !== null) {
        const appliedTheme = document.documentElement.getAttribute('data-wefat-theme');
        if (appliedTheme === 'dark' || appliedTheme === 'light')
            return appliedTheme;
    }
    const matchMedia = environment.window.matchMedia;
    if (matchMedia === undefined) {
        return undefined;
    }
    try {
        const result = Reflect.apply(matchMedia, environment.window, ['(prefers-color-scheme: dark)']);
        return result.matches === true ? 'dark' : 'light';
    }
    catch {
        return undefined;
    }
}
function readCallbacks(api, rawOptions) {
    if (rawOptions === undefined) {
        return { used: false };
    }
    if (!isPlainRecord(rawOptions)) {
        throw optionError(api, 'options must be a plain object');
    }
    for (const key of Reflect.ownKeys(rawOptions)) {
        const descriptor = typeof key === 'string'
            ? Object.getOwnPropertyDescriptor(rawOptions, key)
            : undefined;
        if (typeof key !== 'string' || !CALLBACK_KEYS.has(key)) {
            throw optionError(api, `unsupported option field ${String(key)}`);
        }
        if (descriptor === undefined || !('value' in descriptor)) {
            throw optionError(api, `option field ${key} must be a direct data property`);
        }
        if (descriptor.value !== undefined && typeof descriptor.value !== 'function') {
            throw optionError(api, `${key} must be a function when provided`);
        }
    }
    const success = rawOptions.success;
    const fail = rawOptions.fail;
    const complete = rawOptions.complete;
    return {
        ...(success === undefined ? {} : { success }),
        ...(fail === undefined ? {} : { fail }),
        ...(complete === undefined ? {} : { complete }),
        used: success !== undefined || fail !== undefined || complete !== undefined,
    };
}
function invokeCallback(callback, value, api, kind, report) {
    if (callback === undefined) {
        return;
    }
    try {
        callback(value);
    }
    catch (error) {
        if (report !== undefined) {
            try {
                report(error, api, kind);
                return;
            }
            catch (reportError) {
                queueMicrotask(() => {
                    throw reportError;
                });
                return;
            }
        }
        queueMicrotask(() => {
            throw error;
        });
    }
}
function browserEnvironment() {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return null;
    }
    return {
        window,
        navigator,
        screen: window.screen,
    };
}
export function createBrowserSystemInfoWxApis(options = {}) {
    if (options.onCallbackError !== undefined && typeof options.onCallbackError !== 'function') {
        throw new TypeError('onCallbackError must be a function when provided');
    }
    const resolvedEnvironment = options.environment ?? browserEnvironment();
    if (resolvedEnvironment === null
        || !isObjectLike(resolvedEnvironment.window)
        || !isObjectLike(resolvedEnvironment.navigator)) {
        throw new TypeError('createBrowserSystemInfoWxApis requires a browser environment or injected environment');
    }
    const environment = resolvedEnvironment;
    function windowInfo(api) {
        try {
            const screen = environment.screen ?? environment.window.screen ?? {};
            const screenWidth = finiteNumber(screen.width, finiteNumber(environment.window.innerWidth, 0));
            const screenHeight = finiteNumber(screen.height, finiteNumber(environment.window.innerHeight, 0));
            const windowWidth = finiteNumber(environment.window.innerWidth, screenWidth);
            const windowHeight = finiteNumber(environment.window.innerHeight, screenHeight);
            const safeArea = {
                left: 0,
                right: screenWidth,
                top: 0,
                bottom: screenHeight,
                width: screenWidth,
                height: screenHeight,
            };
            return {
                pixelRatio: finiteNumber(environment.window.devicePixelRatio, 1),
                safeArea,
                screenHeight,
                screenTop: 0,
                screenWidth,
                statusBarHeight: 0,
                windowHeight,
                windowWidth,
            };
        }
        catch (error) {
            throw environmentError(api, 'could not read browser window metrics', error);
        }
    }
    function deviceInfo(api) {
        try {
            const platform = environment.navigator.platform ?? WEB_PLATFORM;
            return {
                abi: 'unknown',
                benchmarkLevel: -1,
                brand: 'browser',
                cpuType: 'unknown',
                deviceAbi: 'unknown',
                memorySize: 'unknown',
                model: 'browser',
                platform: WEB_PLATFORM,
                system: environment.navigator.userAgent ?? platform,
            };
        }
        catch (error) {
            throw environmentError(api, 'could not read browser device information', error);
        }
    }
    function menuButtonRect() {
        const info = windowInfo('getMenuButtonBoundingClientRect');
        const width = 87;
        const height = 32;
        const top = 26;
        const right = Math.max(width, info.windowWidth - 8);
        return {
            bottom: top + height,
            height,
            left: right - width,
            right,
            top,
            width,
        };
    }
    function appBaseInfo(api) {
        try {
            const language = environment.navigator.language ?? 'en';
            const theme = readTheme(environment);
            return {
                PCKernelVersion: 'web',
                SDKVersion: 'web',
                enableDebug: false,
                fontSizeScaleFactor: 1,
                fontSizeSetting: 16,
                host: { appId: '' },
                language,
                ...(theme === undefined ? {} : { theme }),
                version: 'web',
            };
        }
        catch (error) {
            throw environmentError(api, 'could not read browser application information', error);
        }
    }
    function systemSetting(api) {
        try {
            const info = windowInfo(api);
            return {
                bluetoothEnabled: environment.navigator.bluetooth !== undefined,
                deviceOrientation: info.windowWidth > info.windowHeight ? 'landscape' : 'portrait',
                locationEnabled: environment.navigator.geolocation !== undefined,
                wifiEnabled: environment.navigator.onLine !== false,
            };
        }
        catch (error) {
            throw environmentError(api, 'could not read browser system settings', error);
        }
    }
    function authorization() {
        return {
            albumAuthorized: NOT_DETERMINED,
            bluetoothAuthorized: NOT_DETERMINED,
            cameraAuthorized: NOT_DETERMINED,
            locationAuthorized: NOT_DETERMINED,
            locationReducedAccuracy: false,
            microphoneAuthorized: NOT_DETERMINED,
            notificationAlertAuthorized: NOT_DETERMINED,
            notificationAuthorized: NOT_DETERMINED,
            notificationBadgeAuthorized: NOT_DETERMINED,
            notificationSoundAuthorized: NOT_DETERMINED,
            phoneCalendarAuthorized: NOT_DETERMINED,
        };
    }
    function systemInfo(api) {
        const windowResult = windowInfo(api);
        const deviceResult = deviceInfo(api);
        const appResult = appBaseInfo(api);
        const settings = systemSetting(api);
        return {
            ...windowResult,
            ...deviceResult,
            ...appResult,
            ...settings,
            albumAuthorized: false,
            cameraAuthorized: false,
            locationAuthorized: false,
            locationReducedAccuracy: false,
            microphoneAuthorized: false,
            notificationAlertAuthorized: false,
            notificationAuthorized: false,
            notificationBadgeAuthorized: false,
            notificationSoundAuthorized: false,
        };
    }
    function runAsync(rawOptions) {
        let callbacks;
        try {
            callbacks = readCallbacks('getSystemInfo', rawOptions);
        }
        catch (error) {
            return Promise.reject(error instanceof WxSystemInfoRuntimeError
                ? error
                : optionError('getSystemInfo', 'invalid callback options'));
        }
        const task = Promise.resolve().then(() => ({
            ...systemInfo('getSystemInfo'),
            errMsg: 'getSystemInfo:ok',
        }));
        if (!callbacks.used) {
            return task;
        }
        void task.then((result) => {
            invokeCallback(callbacks.success, result, 'getSystemInfo', 'success', options.onCallbackError);
            invokeCallback(callbacks.complete, result, 'getSystemInfo', 'complete', options.onCallbackError);
        }, (error) => {
            const failure = error instanceof WxSystemInfoRuntimeError
                ? error
                : environmentError('getSystemInfo', 'browser system information failed', error);
            invokeCallback(callbacks.fail, failure, 'getSystemInfo', 'fail', options.onCallbackError);
            invokeCallback(callbacks.complete, failure, 'getSystemInfo', 'complete', options.onCallbackError);
        });
        return undefined;
    }
    const apis = {
        getSystemInfo: (rawOptions) => runAsync(rawOptions),
        getSystemInfoSync: () => systemInfo('getSystemInfoSync'),
        getWindowInfo: () => windowInfo('getWindowInfo'),
        getMenuButtonBoundingClientRect: menuButtonRect,
        getDeviceInfo: () => deviceInfo('getDeviceInfo'),
        getAppBaseInfo: () => appBaseInfo('getAppBaseInfo'),
        getSystemSetting: () => systemSetting('getSystemSetting'),
        getAppAuthorizeSetting: authorization,
    };
    return Object.freeze(apis);
}
//# sourceMappingURL=system-info-adapter.js.map